import React, { useState } from 'react';
import { TemplatePreview } from '../components/TemplatePreview';

const FONT_OPTIONS = {
  lato: 'Lato, Arial, sans-serif',
  poppins: 'Poppins, Arial, sans-serif',
  roboto: 'Roboto, Arial, sans-serif',
  georgia: 'Georgia, serif',
};

const FONT_SIZES = {
  small: '12px',
  medium: '14px',
  large: '16px',
};

export function TemplateTest() {
  const [side, setSide] = useState<'front' | 'back'>('front');
  const [cardImage, setCardImage] = useState<string>('https://images.unsplash.com/photo-1518791841217-8f162f1e1131');
  const [message, setMessage] = useState<string>('Thank you for being awesome!\n\nWe really appreciate everything you do.');
  const [recipientName, setRecipientName] = useState<string>('John Doe');
  const [fontFamily, setFontFamily] = useState<keyof typeof FONT_OPTIONS>('lato');
  const [fontSize, setFontSize] = useState<keyof typeof FONT_SIZES>('medium');
  const [textAlignment, setTextAlignment] = useState<'left' | 'center' | 'right'>('left');
  
  const sampleAddress = {
    address_line1: '123 Main St',
    address_line2: 'Apt 456',
    address_city: 'San Francisco',
    address_state: 'CA',
    address_zip: '94105'
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Template Test Page</h1>
      
      <div className="flex mb-4 space-x-4">
        <button 
          onClick={() => setSide('front')} 
          className={`px-4 py-2 ${side === 'front' ? 'bg-brand-primary text-white' : 'bg-gray-200'}`}
        >
          Front Side
        </button>
        <button 
          onClick={() => setSide('back')} 
          className={`px-4 py-2 ${side === 'back' ? 'bg-brand-primary text-white' : 'bg-gray-200'}`}
        >
          Back Side
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Controls</h2>
          
          {side === 'front' && (
            <div>
              <label className="block mb-2">Card Image URL:</label>
              <input 
                type="text" 
                value={cardImage} 
                onChange={(e) => setCardImage(e.target.value)}
                className="w-full p-2 border"
              />
            </div>
          )}
          
          {side === 'back' && (
            <>
              <div>
                <label className="block mb-2">Recipient Name:</label>
                <input 
                  type="text" 
                  value={recipientName} 
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="w-full p-2 border"
                />
              </div>
              
              <div>
                <label className="block mb-2">Message:</label>
                <textarea 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full p-2 border min-h-[150px]"
                />
              </div>
              
              <div>
                <label className="block mb-2">Font Family:</label>
                <select 
                  value={fontFamily} 
                  onChange={(e) => setFontFamily(e.target.value as keyof typeof FONT_OPTIONS)}
                  className="w-full p-2 border"
                >
                  {Object.entries(FONT_OPTIONS).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block mb-2">Font Size:</label>
                <select 
                  value={fontSize} 
                  onChange={(e) => setFontSize(e.target.value as keyof typeof FONT_SIZES)}
                  className="w-full p-2 border"
                >
                  {Object.entries(FONT_SIZES).map(([key, value]) => (
                    <option key={key} value={key}>{key} ({value})</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block mb-2">Text Alignment:</label>
                <div className="flex space-x-4">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      onClick={() => setTextAlignment(align as 'left' | 'center' | 'right')}
                      className={`px-4 py-2 ${textAlignment === align ? 'bg-brand-primary text-white' : 'bg-gray-200'}`}
                    >
                      {align}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div>
          <h2 className="text-xl font-semibold mb-4">Preview</h2>
          <div className="border rounded p-4 bg-white">
            <TemplatePreview
              side={side}
              cardImage={cardImage}
              message={message}
              recipientName={recipientName}
              recipientAddress={sampleAddress}
              textStyle={{
                font: FONT_OPTIONS[fontFamily],
                alignment: textAlignment,
                fontSize: FONT_SIZES[fontSize]
              }}
            />
          </div>
          
          <div className="mt-4 p-4 bg-gray-100 rounded">
            <h3 className="font-medium mb-2">Debug Info:</h3>
            <pre className="text-xs overflow-auto max-h-[200px]">
              {JSON.stringify({
                side,
                cardImage: cardImage?.substring(0, 30) + '...',
                messagePreview: message?.substring(0, 30) + '...',
                recipientName,
                textStyle: {
                  font: FONT_OPTIONS[fontFamily],
                  alignment: textAlignment,
                  fontSize: FONT_SIZES[fontSize]
                }
              }, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateTest; 